import React, { useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import AssistantMarkdown, { countMarkdownBlocks } from './AssistantMarkdown';
import type { ChatMessage } from './types';
import ChatBubbleDisclosure from '../ChatBubbleDisclosure';

interface Props {
  messages: ChatMessage[];
  /**
   * When true, render an extra assistant-style bubble at the end of the
   * thread containing a three-dot typing animation. Replaces the send-button
   * spinner used previously.
   */
  loading?: boolean;
  /**
   * Empty-state placeholder text shown when `messages` is empty and the
   * thread is not loading. Defaults to FarChat's procurement-specific copy.
   * Pass `null` to suppress the placeholder entirely (e.g. HomeChat, or
   * when the parent has v2 turn content that ChatThread cannot see).
   */
  emptyPlaceholder?: React.ReactNode | null;
  /** Embed a single legacy bubble in a parent-owned mixed conversation timeline. */
  compact?: boolean;
  bubbleDisclosure?: { label: string; defaultExpanded: boolean };
  /**
   * Optional content for the canonical assistant loading bubble.  Callers
   * with a server-routed workflow can name the work in progress without
   * recreating a lookalike bubble (and drifting from Home Chat's tokens).
   */
  loadingContent?: React.ReactNode;
  /** Accessible name for the canonical assistant loading bubble. */
  loadingAriaLabel?: string;
}

const DEFAULT_EMPTY_PLACEHOLDER = (
  <Typography variant="body2">
    Ask a question about this procurement&hellip;
  </Typography>
);

/**
 * Legacy-fallback block count used when an assistant message has no
 * structured `tldr` field. Set to 1 so the legacy path also collapses to
 * a single paragraph — matching the structured TLDR-only collapsed view
 * for older threads persisted before structured fields were saved.
 * Structured messages skip this entirely.
 */
const COLLAPSED_BLOCK_COUNT = 1;

/**
 * True when the assistant message carries the structured shape
 * (`tldr` non-empty). Such messages render via the structured path and
 * never use the legacy block-clamp.
 */
function isStructured(m: ChatMessage): boolean {
  return typeof m.tldr === 'string' && m.tldr.length > 0;
}

const ChatThread: React.FC<Props> = ({
  messages,
  loading = false,
  emptyPlaceholder = DEFAULT_EMPTY_PLACEHOLDER,
  compact = false,
  bubbleDisclosure,
  loadingContent,
  loadingAriaLabel = 'Assistant is typing',
}) => {
  const theme = useTheme();
  // Per-message expansion state keyed by message index. Not persisted across
  // popup close/reopen — fresh hydrated threads re-default to collapsed for
  // assistant messages that have detail to reveal.
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  // Memoize the block count per LEGACY-path assistant message so we don't
  // re-parse markdown on every render. Structured messages skip this — their
  // collapse decision is based on detail presence, not block count.
  const blockCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    messages.forEach((m, idx) => {
      if (m.role === 'assistant' && !m.refusal && !isStructured(m)) {
        counts[idx] = countMarkdownBlocks(m.content);
      }
    });
    return counts;
  }, [messages]);

  if ((!messages || messages.length === 0) && !loading) {
    // Suppress entirely when caller passes `null` — used by HomeChat (no
    // procurement context) and by FarChatPopup when v2 `turns` already hold
    // visible content that lives outside `messages`.
    if (emptyPlaceholder === null) {
      return null;
    }
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          p: 3,
          color: 'text.secondary',
        }}
      >
        {emptyPlaceholder}
      </Box>
    );
  }

  return (
    <Box sx={{ p: compact ? 0 : 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      {(messages || []).map((m, idx) => {
        const isUser = m.role === 'user';
        const isAssistant = m.role === 'assistant';
        const isRefusal = !!m.refusal;
        const bg = isUser
          ? 'primary.main'
          : isRefusal
          ? alpha(theme.palette.warning.light, 0.7)
          : 'surfaceMuted';
        const color = isUser ? 'primary.contrastText' : 'text.primary';

        // Collapsibility:
        //   - Structured messages (have `tldr`): collapsible iff there is
        //     `detail` to reveal.
        //   - Legacy messages: collapsible iff content exceeds
        //     COLLAPSED_BLOCK_COUNT top-level markdown blocks.
        const structured = isAssistant && !isRefusal && isStructured(m);
        const hasDetail = !!(m.detail && m.detail.length > 0);
        const collapsible = isAssistant && !isRefusal && (
          structured
            ? hasDetail
            : (blockCounts[idx] ?? 0) > COLLAPSED_BLOCK_COUNT
        );
        const isExpanded = !!expanded[idx];
        // Only the legacy path uses the block-clamp prop on AssistantMarkdown.
        const legacyMaxBlocks =
          !structured && collapsible && !isExpanded
            ? COLLAPSED_BLOCK_COUNT
            : undefined;

        return (
          <Box
            key={idx}
            data-role={m.role}
            data-refusal={isRefusal || undefined}
            sx={{
              alignSelf: isUser ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
              px: 1.5,
              py: 1,
              borderRadius: 2,
              bgcolor: bg,
              color,
              // User + system messages keep pre-wrap so literal newlines are
              // preserved. Assistant messages render markdown and should
              // follow normal block flow.
              whiteSpace: isAssistant ? 'normal' : 'pre-wrap',
              fontSize: '0.9rem',
              boxShadow: isUser ? 0 : 1,
              border: isRefusal ? `1px solid ${alpha(theme.palette.warning.main, 0.4)}` : undefined,
              // Keep CitationMarker popovers readable inside assistant
              // bubbles where we render markdown.
              wordBreak: 'break-word',
            }}
          >
            {isAssistant ? (
              structured ? (
                <>
                  <AssistantMarkdown
                    text={m.tldr || ''}
                    citations={m.citations}
                  />
                  {isExpanded && hasDetail && (
                    <Box sx={{ mt: 2 }}>
                      <AssistantMarkdown
                        text={m.detail || ''}
                        citations={m.citations}
                      />
                    </Box>
                  )}
                </>
              ) : (
                <AssistantMarkdown
                  text={m.content}
                  citations={m.citations}
                  maxBlocks={legacyMaxBlocks}
                />
              )
            ) : (
              bubbleDisclosure ? <ChatBubbleDisclosure label={bubbleDisclosure.label} preview={m.content} defaultExpanded={bubbleDisclosure.defaultExpanded}>{m.content}</ChatBubbleDisclosure> : m.content
            )}
            {collapsible && (
              <Box sx={{ mt: 0.5 }}>
                <Button
                  size="small"
                  variant="text"
                  aria-expanded={isExpanded}
                  onClick={() =>
                    setExpanded((prev) => ({ ...prev, [idx]: !prev[idx] }))
                  }
                  sx={{
                    textTransform: 'none',
                    p: 0,
                    minWidth: 0,
                    fontSize: '0.85rem',
                    fontWeight: 500,
                  }}
                >
                  {isExpanded ? 'Show Less' : 'Show More'}
                </Button>
              </Box>
            )}
          </Box>
        );
      })}
      {loading && (
        <Box
          data-testid="farchat-loading-bubble"
          data-role="assistant"
          aria-live="polite"
          aria-label={loadingAriaLabel}
          sx={{
            alignSelf: 'flex-start',
            maxWidth: '85%',
            px: 1.5,
            py: 1,
            borderRadius: 2,
            bgcolor: 'surfaceMuted',
            color: 'text.primary',
            fontSize: '0.9rem',
            boxShadow: 1,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          {loadingContent ?? [0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                bgcolor: 'text.secondary',
                opacity: 0.6,
                animation: 'farchat-typing 1.2s infinite ease-in-out',
                animationDelay: `${i * 0.15}s`,
                '@keyframes farchat-typing': {
                  '0%, 60%, 100%': {
                    transform: 'translateY(0)',
                    opacity: 0.3,
                  },
                  '30%': {
                    transform: 'translateY(-3px)',
                    opacity: 0.9,
                  },
                },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default ChatThread;
